import { useState, useRef, useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import {
  PlusCircle,
  Bot,
  MessageSquarePlus,
  Star,
  Send,
  CheckCircle,
  Check,
  Paperclip,
  Image,
  X,
  FileText,
  Film,
  AlertCircle,
  Loader2,
  PlayCircle,
  RefreshCw,
  ServerOff,
  Mail,
  Calendar,
  Shield,
  LogOut,
  Settings,
  CreditCard,
  Zap,
  Trash2,
  Phone,
  ExternalLink,
  Clock,
  BookOpen,
  ChevronRight,
  Search,
  Sparkles,
  MessageCircle,
  MessageSquare,
  Database,
  TestTube,
  Rocket,
  BarChart3,
  ArrowLeft,
  Bug,
  Lightbulb,
  Palette,
  Smile,
  Meh,
  Wrench,
  Heart,
  PartyPopper,
} from "lucide-react";
import { useApp } from "../context/AppContext";
import DashboardSidebar, { AgentCard } from "../components/DashboardSidebar";
import Button from "../components/Button";
import EmptyState from "../components/EmptyState";
import Dialog from "../components/Dialog";
import api from "../services/api";

export default function DashboardLayout() {
  return (
    <div className="flex min-h-screen bg-background">
      <DashboardSidebar />
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}

// Step names for display
const stepNames = [
  "Name",
  "Behavior",
  "Knowledge",
  "AI Model",
  "Features",
  "Training",
  "Test AI",
  "Connect WhatsApp",
  "Pair QR",
  "Go Live",
];

const RETRY_INTERVAL = 10; // seconds

export function DashboardHome() {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [retryCountdown, setRetryCountdown] = useState(0);
  const [retryCount, setRetryCount] = useState(0);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loadingAnalytics, setLoadingAnalytics] = useState(new Set());

  // Fetch analytics for a single agent
  const fetchAgentAnalytics = async (agentId) => {
    try {
      const analyticsResult = await api.getAnalytics(agentId, "30d");
      const analytics = analyticsResult.analytics || {};
      return {
        total_conversations: analytics.total_conversations || 0,
        leads_collected: analytics.total_leads || 0,
        messages_handled: analytics.total_messages || 0,
      };
    } catch (err) {
      console.error(`Failed to fetch analytics for agent ${agentId}:`, err);
      return null;
    }
  };

  // Fetch analytics for all completed agents
  const fetchAllAnalytics = async (agentsList) => {
    const completedAgentIds = agentsList
      .filter((a) => a.status !== "draft" || (a.current_step || 0) >= 10)
      .map((a) => a.id);

    if (completedAgentIds.length === 0) return;

    // Mark all completed agents as loading analytics
    setLoadingAnalytics(new Set(completedAgentIds));

    // Fetch analytics for each agent in parallel
    await Promise.all(
      completedAgentIds.map(async (agentId) => {
        const stats = await fetchAgentAnalytics(agentId);

        // Update the agent with stats and remove from loading set
        setAgents((prev) =>
          prev.map((agent) =>
            agent.id === agentId && stats ? { ...agent, stats } : agent,
          ),
        );

        setLoadingAnalytics((prev) => {
          const next = new Set(prev);
          next.delete(agentId);
          return next;
        });
      }),
    );
  };

  // Fetch agents function - now loads agents first, then analytics separately
  const fetchAgents = async () => {
    try {
      setFetchError(null);
      setIsLoading(true);
      const result = await api.getAgents();
      const agentsList = result.agents || [];

      // Set agents immediately without analytics
      setAgents(agentsList);
      setRetryCount(0); // Reset retry count on success
      setIsLoading(false);

      // Then fetch analytics separately (non-blocking)
      fetchAllAnalytics(agentsList);
    } catch (err) {
      console.error("Failed to fetch agents:", err);
      setFetchError(err.message);
      setAgents([]);
      setRetryCountdown(RETRY_INTERVAL); // Start countdown for auto-retry
      setIsLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchAgents();
  }, []);

  // Auto-retry countdown
  useEffect(() => {
    if (retryCountdown <= 0 || !fetchError) return;

    const timer = setInterval(() => {
      setRetryCountdown((prev) => {
        if (prev <= 1) {
          // Time to retry
          setRetryCount((c) => c + 1);
          fetchAgents();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [retryCountdown, fetchError]);

  // Separate incomplete (draft) agents from completed ones
  const incompleteAgents = agents.filter(
    (a) => a.status === "draft" && (a.current_step || 0) < 10,
  );
  const completedAgents = agents.filter(
    (a) => a.status !== "draft" || (a.current_step || 0) >= 10,
  );

  const handleContinueAgent = (agent) => {
    const step = agent.current_step || 1;
    const stepPaths = [
      "name",
      "behavior",
      "knowledge",
      "model",
      "features",
      "training",
      "test",
      "connect",
      "pair",
      "golive",
    ];
    const stepPath = stepPaths[Math.min(step, stepPaths.length - 1)];
    navigate(`/create/${stepPath}?agentId=${agent.id}`);
  };

  const handleRetry = () => {
    setRetryCountdown(0); // Cancel any pending auto-retry
    setRetryCount((c) => c + 1);
    fetchAgents();
  };

  const handleDeleteClick = (agent) => {
    setAgentToDelete(agent);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!agentToDelete) return;

    setIsDeleting(true);
    try {
      await api.deleteAgent(agentToDelete.id);
      // Remove the agent from local state
      setAgents((prev) => prev.filter((a) => a.id !== agentToDelete.id));
      setShowDeleteDialog(false);
      setAgentToDelete(null);
    } catch (err) {
      console.error("Failed to delete agent:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false);
    setAgentToDelete(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
          <p className="text-text-muted">
            {retryCount > 0
              ? `Retrying... (attempt ${retryCount + 1})`
              : "Loading agents..."}
          </p>
        </div>
      </div>
    );
  }

  // Show server unavailable state
  if (fetchError) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <ServerOff className="w-8 h-8 text-amber-500" />
          </div>
          <h2 className="text-xl font-bold text-text-primary mb-2">
            Server Unavailable
          </h2>
          <p className="text-text-muted mb-2">
            Unable to connect to the server. This could be due to maintenance or
            the server being offline.
          </p>
          {retryCount > 0 && (
            <p className="text-xs text-text-muted mb-4">
              Attempted {retryCount} {retryCount === 1 ? "retry" : "retries"}
            </p>
          )}
          <div className="flex items-center justify-center gap-2 mb-6">
            <Loader2 className="w-4 h-4 text-primary animate-spin" />
            <p className="text-sm text-primary">
              Retrying in {retryCountdown}s...
            </p>
          </div>
          <Button icon={RefreshCw} onClick={handleRetry}>
            Retry Now
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">My Agents</h1>
          <p className="text-text-secondary mt-1">
            Manage your AI-powered WhatsApp chatbots
          </p>
        </div>
        <Button icon={PlusCircle} onClick={() => navigate("/create")}>
          Create New Agent
        </Button>
      </div>

      {/* Incomplete Agents Section */}
      {incompleteAgents.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-text-primary mb-4">
            Continue Setup
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {incompleteAgents.map((agent, index) => (
              <div
                key={agent.id}
                className="card p-5 border-2 border-dashed border-amber-300 bg-amber-50/50 animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                      <Bot className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-text-primary">
                        {agent.name || "Unnamed Agent"}
                      </h3>
                      <p className="text-xs text-text-muted">
                        Step {agent.current_step || 1} of 10:{" "}
                        {stepNames[(agent.current_step || 1) - 1]}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(agent);
                    }}
                    className="p-2 text-text-muted hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete agent"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {/* Progress bar */}
                <div className="mb-4">
                  <div className="h-2 bg-amber-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-amber-500 rounded-full transition-all"
                      style={{
                        width: `${((agent.current_step || 1) / 10) * 100}%`,
                      }}
                    />
                  </div>
                  <p className="text-xs text-amber-600 mt-1">
                    {Math.round(((agent.current_step || 1) / 10) * 100)}%
                    complete
                  </p>
                </div>
                <Button
                  variant="secondary"
                  size="small"
                  icon={PlayCircle}
                  onClick={() => handleContinueAgent(agent)}
                  className="w-full"
                >
                  Continue Setup
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Completed Agents Grid */}
      {agents.length === 0 ? (
        <div className="max-w-3xl mx-auto">
          {/* What is an AI Agent - Educational Section */}
          <div className="card p-8 mb-6 bg-gradient-to-br from-primary/5 via-white to-accent/5 border-primary/20">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent mb-4">
                <Bot className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-text-primary mb-2">
                What is an AI Agent?
              </h2>
              <p className="text-text-secondary max-w-xl mx-auto">
                An AI agent is like a smart virtual assistant that can chat with
                your customers on WhatsApp 24/7, even when you're busy or
                asleep.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="text-center p-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mx-auto mb-3">
                  <MessageCircle className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-text-primary mb-1">
                  Answers Questions
                </h3>
                <p className="text-sm text-text-muted">
                  Instantly responds to customer questions about your products,
                  services, or business
                </p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center mx-auto mb-3">
                  <Clock className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-semibold text-text-primary mb-1">
                  Works 24/7
                </h3>
                <p className="text-sm text-text-muted">
                  Never sleeps or takes breaks — your customers get help
                  anytime, day or night
                </p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mx-auto mb-3">
                  <Database className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="font-semibold text-text-primary mb-1">
                  Learns Your Business
                </h3>
                <p className="text-sm text-text-muted">
                  You teach it about your business by uploading documents, and
                  it uses that knowledge to help customers
                </p>
              </div>
            </div>

            <div className="bg-white/70 rounded-xl p-5 border border-border mb-6">
              <h3 className="font-semibold text-text-primary mb-3 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-500" />
                How it works
              </h3>
              <ol className="space-y-3 text-sm text-text-secondary">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">
                    1
                  </span>
                  <span>
                    <strong className="text-text-primary">
                      You create an agent
                    </strong>{" "}
                    — give it a name and tell it what your business does
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">
                    2
                  </span>
                  <span>
                    <strong className="text-text-primary">You teach it</strong>{" "}
                    — upload documents (like FAQs, price lists, product info) so
                    it knows your business
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">
                    3
                  </span>
                  <span>
                    <strong className="text-text-primary">
                      You connect WhatsApp
                    </strong>{" "}
                    — scan a QR code to link your WhatsApp number
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">
                    4
                  </span>
                  <span>
                    <strong className="text-text-primary">
                      It starts helping customers
                    </strong>{" "}
                    — when someone messages you, the AI replies automatically!
                  </span>
                </li>
              </ol>
            </div>

            <div className="text-center">
              <Button
                icon={PlusCircle}
                onClick={() => navigate("/create")}
                size="large"
              >
                Create Your First Agent
              </Button>
              <p className="text-xs text-text-muted mt-3">
                No coding required • Takes about 10 minutes
              </p>
            </div>
          </div>
        </div>
      ) : completedAgents.length > 0 ? (
        <>
          {incompleteAgents.length > 0 && (
            <h2 className="text-lg font-semibold text-text-primary mb-4">
              Active Agents
            </h2>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {completedAgents.map((agent, index) => (
              <div
                key={agent.id}
                className="animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <AgentCard
                  agent={agent}
                  onClick={() => navigate(`/agent/${agent.id}`)}
                  loadingStats={loadingAnalytics.has(agent.id)}
                />
              </div>
            ))}
          </div>
        </>
      ) : incompleteAgents.length === 0 ? (
        <EmptyState
          icon={Bot}
          title="No agents yet"
          description="Create your first AI agent to start automating your WhatsApp conversations."
          action={() => navigate("/create")}
          actionLabel="Create Your First Agent"
          actionIcon={PlusCircle}
        />
      ) : null}

      {/* Delete Agent Confirmation Dialog */}
      <Dialog
        isOpen={showDeleteDialog}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Agent"
        message={`Are you sure you want to delete "${agentToDelete?.name || "this agent"}"? This action cannot be undone and all associated data will be permanently removed.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}

export function ProfilePage() {
  const { user, signOut } = useApp();
  const navigate = useNavigate();
  const [showSignOutDialog, setShowSignOutDialog] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await signOut();
      navigate("/");
    } finally {
      setIsSigningOut(false);
      setShowSignOutDialog(false);
    }
  };

  // Format date for display (handles Firestore timestamps and ISO strings)
  const formatDate = (dateValue) => {
    if (!dateValue) return "N/A";

    let date;
    // Handle Firestore Timestamp object (has _seconds or seconds property)
    if (dateValue._seconds !== undefined) {
      date = new Date(dateValue._seconds * 1000);
    } else if (dateValue.seconds !== undefined) {
      date = new Date(dateValue.seconds * 1000);
    } else if (typeof dateValue === "string") {
      date = new Date(dateValue);
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else {
      return "N/A";
    }

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const memberSince = formatDate(user?.created_at);

  const accountDetails = [
    { icon: Mail, label: "Email", value: user?.email || "demo@botibonga.com" },
    {
      icon: Calendar,
      label: "Member Since",
      value: memberSince !== "N/A" ? memberSince : "January 2025",
    },
    {
      icon: Shield,
      label: "Account Status",
      value: "Active",
      valueClass: "text-success",
    },
  ];

  return (
    <div className="p-8 animate-fade-in max-w-4xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text-primary">My Profile</h1>
        <p className="text-text-secondary mt-1">
          Manage your account settings and preferences
        </p>
      </div>

      <div className="grid gap-6">
        {/* Profile Card */}
        <div className="card p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-5">
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt={user?.name || "User"}
                  className="w-20 h-20 rounded-2xl object-cover border-2 border-border"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-lg">
                  <span className="text-3xl font-bold text-white">
                    {user?.name?.charAt(0) || "U"}
                  </span>
                </div>
              )}
              <div>
                <h2 className="text-xl font-bold text-text-primary">
                  {user?.name || "Demo User"}
                </h2>
                <p className="text-text-muted mt-1">
                  {user?.email || "demo@botibonga.com"}
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-primary/10 to-accent/10 text-primary border border-primary/20">
                    <Zap className="w-3 h-3" />
                    Beta Tester
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-success/10 text-success">
                    <CheckCircle className="w-3 h-3" />
                    Verified
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowSignOutDialog(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-xl transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Account Details */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5 text-text-muted" />
              Account Details
            </h3>
            <div className="space-y-4">
              {accountDetails.map(
                ({ icon: Icon, label, value, valueClass }) => (
                  <div
                    key={label}
                    className="flex items-center justify-between py-3 border-b border-border last:border-0"
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="w-4 h-4 text-text-muted" />
                      <span className="text-sm text-text-secondary">
                        {label}
                      </span>
                    </div>
                    <span
                      className={`text-sm font-medium ${valueClass || "text-text-primary"}`}
                    >
                      {value}
                    </span>
                  </div>
                ),
              )}
            </div>
          </div>

          {/* Subscription */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-text-muted" />
              Subscription
            </h3>
            <div className="p-4 bg-gradient-to-br from-primary/5 to-accent/5 rounded-xl border border-primary/10 mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-primary">
                  Beta Access
                </span>
                <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full font-medium">
                  FREE
                </span>
              </div>
              <p className="text-xs text-text-muted">
                You have full access to all features during the beta period.
                Thank you for being an early adopter!
              </p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-text-secondary">
                <CheckCircle className="w-4 h-4 text-success" />
                <span>Unlimited agents</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-text-secondary">
                <CheckCircle className="w-4 h-4 text-success" />
                <span>All AI models</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-text-secondary">
                <CheckCircle className="w-4 h-4 text-success" />
                <span>Priority support</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-text-secondary">
                <CheckCircle className="w-4 h-4 text-success" />
                <span>Advanced analytics</span>
              </div>
            </div>
          </div>
        </div>

        {/* Beta Program Banner */}
        <div className="card p-6 bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 border-primary/20">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center flex-shrink-0">
              <Star className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-text-primary mb-1">
                You're Part of Something Special!
              </h3>
              <p className="text-sm text-text-secondary mb-3">
                As a beta tester, your feedback directly shapes the future of
                Botibonga. We're building this together, and your insights are
                invaluable.
              </p>
              <Button
                variant="secondary"
                size="small"
                onClick={() => navigate("/dashboard/feedback")}
              >
                Share Feedback
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Sign Out Confirmation Dialog */}
      <Dialog
        isOpen={showSignOutDialog}
        onClose={() => setShowSignOutDialog(false)}
        onConfirm={handleSignOut}
        title="Sign Out"
        message="Are you sure you want to sign out? You'll need to sign in again to access your agents."
        confirmText="Sign Out"
        cancelText="Cancel"
        variant="danger"
        isLoading={isSigningOut}
      />
    </div>
  );
}

export function HelpPage() {
  const navigate = useNavigate();

  return (
    <div className="p-8 animate-fade-in">
      <h1 className="text-2xl font-bold text-text-primary mb-8">
        Help & Support
      </h1>

      <div className="grid gap-6 max-w-2xl">
        {/* Documentation */}
        <div className="card p-6">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="w-5 h-5 text-primary" />
            <h3 className="font-semibold text-text-primary">Documentation</h3>
          </div>
          <p className="text-text-muted text-sm mb-4">
            Learn how to get the most out of Botibonga Studio with our
            comprehensive guides.
          </p>
          <Button
            variant="secondary"
            size="small"
            icon={ExternalLink}
            onClick={() => window.open("/dashboard/docs", "_blank")}
          >
            View Docs
          </Button>
        </div>

        {/* Chat Support */}
        <div className="card p-6">
          <div className="flex items-center gap-2 mb-2">
            <MessageCircle className="w-5 h-5 text-green-500" />
            <h3 className="font-semibold text-text-primary">Chat Support</h3>
          </div>
          <p className="text-text-muted text-sm mb-4">
            Need help? Our support team is here to assist you.
          </p>
          <div className="space-y-3">
            <a
              href="https://wa.me/254717811921"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors group"
            >
              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-text-primary">WhatsApp / Call</p>
                <p className="text-sm text-green-600">+254 717 811 921</p>
              </div>
              <ExternalLink className="w-4 h-4 text-text-muted group-hover:text-green-600 transition-colors" />
            </a>
            <a
              href="mailto:sales@botibonga.com"
              className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors group"
            >
              <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-text-primary">Email</p>
                <p className="text-sm text-blue-600">sales@botibonga.com</p>
              </div>
              <ExternalLink className="w-4 h-4 text-text-muted group-hover:text-blue-600 transition-colors" />
            </a>
          </div>
        </div>

        {/* Video Tutorials */}
        <div className="card p-6">
          <div className="flex items-center gap-2 mb-2">
            <PlayCircle className="w-5 h-5 text-purple-500" />
            <h3 className="font-semibold text-text-primary">Video Tutorials</h3>
          </div>
          <p className="text-text-muted text-sm mb-4">
            Watch step-by-step tutorials to master every feature.
          </p>
          <div className="flex items-center gap-2 px-4 py-3 bg-purple-50 rounded-lg border border-purple-200">
            <Clock className="w-5 h-5 text-purple-500" />
            <span className="text-purple-700 font-medium">Coming Soon</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Icon mapping for documentation sections (used when rendering fetched data)
const SECTION_ICONS = {
  "getting-started": { icon: Rocket, color: "bg-green-500" },
  "agent-setup": { icon: Bot, color: "bg-primary" },
  "knowledge-base": { icon: Database, color: "bg-blue-500" },
  features: { icon: Sparkles, color: "bg-purple-500" },
  whatsapp: { icon: MessageCircle, color: "bg-green-600" },
  testing: { icon: TestTube, color: "bg-orange-500" },
  analytics: { icon: BarChart3, color: "bg-cyan-500" },
};

// Helper function to get icon and color based on section title
const getSectionStyle = (section) => {
  // First try to match by section id
  if (SECTION_ICONS[section.id]) {
    return SECTION_ICONS[section.id];
  }

  // Fall back to matching by title keywords
  const title = section.title?.toLowerCase() || "";
  if (title.includes("getting started")) {
    return { icon: Rocket, color: "bg-green-500" };
  } else if (title.includes("agent") || title.includes("configuration")) {
    return { icon: Bot, color: "bg-primary" };
  } else if (title.includes("knowledge")) {
    return { icon: Database, color: "bg-blue-500" };
  } else if (title.includes("feature")) {
    return { icon: Sparkles, color: "bg-purple-500" };
  } else if (title.includes("whatsapp")) {
    return { icon: MessageCircle, color: "bg-green-600" };
  } else if (title.includes("test")) {
    return { icon: TestTube, color: "bg-orange-500" };
  } else if (title.includes("analytics") || title.includes("report")) {
    return { icon: BarChart3, color: "bg-cyan-500" };
  }

  // Default
  return { icon: BookOpen, color: "bg-gray-500" };
};

export function DocsPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSection, setActiveSection] = useState(null);
  const [activeArticle, setActiveArticle] = useState(0);
  const [sections, setSections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch documentation from backend
  useEffect(() => {
    const fetchDocs = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await api.getDocs();
        if (response.sections && response.sections.length > 0) {
          setSections(response.sections);
          setActiveSection(response.sections[0].id);
        }
      } catch (err) {
        console.error("Failed to fetch documentation:", err);
        setError("Failed to load documentation. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocs();
  }, []);

  // Filter sections and articles based on search
  const filteredSections = searchQuery
    ? sections
        .map((section) => ({
          ...section,
          articles: (section.articles || []).filter(
            (article) =>
              article.title
                ?.toLowerCase()
                .includes(searchQuery.toLowerCase()) ||
              article.content
                ?.toLowerCase()
                .includes(searchQuery.toLowerCase()),
          ),
        }))
        .filter((section) => section.articles.length > 0)
    : sections;

  const currentSection =
    filteredSections.find((s) => s.id === activeSection) || filteredSections[0];
  const currentArticle = currentSection?.articles?.[activeArticle] || null;

  // Reset article index when section changes
  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    setActiveArticle(0);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
          <p className="text-text-muted">Loading documentation...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-text-primary mb-2">
            Unable to Load Documentation
          </h2>
          <p className="text-text-muted mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>Try Again</Button>
        </div>
      </div>
    );
  }

  // No documentation available
  if (sections.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md">
          <BookOpen className="w-16 h-16 text-text-muted mx-auto mb-4" />
          <h2 className="text-xl font-bold text-text-primary mb-2">
            No Documentation Available
          </h2>
          <p className="text-text-muted mb-4">
            Documentation content is being prepared. Please check back later.
          </p>
          <Button
            variant="secondary"
            onClick={() => navigate("/dashboard/help")}
          >
            Back to Help
          </Button>
        </div>
      </div>
    );
  }

  // Get style for current section
  const currentSectionStyle = currentSection
    ? getSectionStyle(currentSection)
    : null;
  const CurrentSectionIcon = currentSectionStyle?.icon || BookOpen;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-white border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/dashboard/help")}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-text-muted" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-text-primary">
                Documentation
              </h1>
              <p className="text-sm text-text-muted">
                Learn how to use Botibonga Studio
              </p>
            </div>
          </div>

          {/* Search */}
          <div className="mt-4 relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              placeholder="Search documentation..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setActiveArticle(0);
              }}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-8">
          {/* Sidebar Navigation */}
          <div className="w-64 flex-shrink-0">
            <nav className="sticky top-32 space-y-2">
              {filteredSections.map((section) => {
                const sectionStyle = getSectionStyle(section);
                const Icon = sectionStyle.icon;
                const isActive = section.id === activeSection;
                return (
                  <button
                    key={section.id}
                    onClick={() => handleSectionChange(section.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "hover:bg-gray-100 text-text-secondary"
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        isActive ? sectionStyle.color : "bg-gray-200"
                      }`}
                    >
                      <Icon
                        className={`w-4 h-4 ${isActive ? "text-white" : "text-gray-600"}`}
                      />
                    </div>
                    <span className="font-medium text-sm">{section.title}</span>
                    {isActive && (
                      <ChevronRight className="w-4 h-4 ml-auto text-primary" />
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {currentSection && (
              <>
                {/* Section Header */}
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center ${currentSectionStyle?.color || "bg-gray-500"}`}
                    >
                      <CurrentSectionIcon className="w-5 h-5 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-text-primary">
                      {currentSection.title}
                    </h2>
                  </div>
                </div>

                {/* Article Tabs */}
                <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                  {(currentSection.articles || []).map((article, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveArticle(index)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                        activeArticle === index
                          ? "bg-primary text-white"
                          : "bg-gray-100 text-text-secondary hover:bg-gray-200"
                      }`}
                    >
                      {article.title}
                    </button>
                  ))}
                </div>

                {/* Article Content */}
                {currentArticle && (
                  <div className="card p-8">
                    <h3 className="text-xl font-bold text-text-primary mb-6">
                      {currentArticle.title}
                    </h3>

                    {/* Screenshot from backend */}
                    {currentArticle.screenshot && (
                      <div className="mb-6">
                        <img
                          src={currentArticle.screenshot}
                          alt={`Screenshot for ${currentArticle.title}`}
                          className="max-w-full rounded-xl border border-border shadow-sm"
                        />
                      </div>
                    )}

                    <div className="prose prose-gray max-w-none">
                      {(currentArticle.content || "")
                        .split("\n\n")
                        .map((paragraph, i) => {
                          // Check if it's a screenshot placeholder - skip if we already have a real screenshot
                          const screenshotMatch = paragraph
                            .trim()
                            .match(/^\[SCREENSHOT:\s*(.+?)\]$/);
                          if (screenshotMatch) {
                            // If we have a real screenshot, skip the placeholder
                            if (currentArticle.screenshot) {
                              return null;
                            }
                            return (
                              <div
                                key={i}
                                className="my-6 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 p-8"
                              >
                                <div className="flex flex-col items-center justify-center text-center">
                                  <div className="w-16 h-16 rounded-xl bg-gray-200 flex items-center justify-center mb-4">
                                    <svg
                                      className="w-8 h-8 text-gray-400"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={1.5}
                                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                      />
                                    </svg>
                                  </div>
                                  <p className="text-sm font-medium text-gray-500 mb-1">
                                    Screenshot Placeholder
                                  </p>
                                  <p className="text-xs text-gray-400">
                                    {screenshotMatch[1]}
                                  </p>
                                </div>
                              </div>
                            );
                          }

                          // Check if it's a header (wrapped in **)
                          if (
                            paragraph.startsWith("**") &&
                            paragraph.endsWith("**")
                          ) {
                            return (
                              <h4
                                key={i}
                                className="font-semibold text-text-primary text-base mt-6 mb-3"
                              >
                                {paragraph.replace(/\*\*/g, "")}
                              </h4>
                            );
                          }

                          // Check if paragraph contains a list (lines starting with •, ✓, or numbers)
                          const lines = paragraph.split("\n");
                          const isList = lines.some(
                            (line) =>
                              line.trim().startsWith("•") ||
                              line.trim().startsWith("✓") ||
                              /^\d+\.\s/.test(line.trim()),
                          );

                          if (isList) {
                            return (
                              <div key={i} className="mb-4">
                                {lines.map((line, j) => {
                                  const trimmedLine = line.trim();
                                  if (!trimmedLine) return null;

                                  // Numbered step (1., 2., etc.)
                                  const numberedMatch =
                                    trimmedLine.match(/^(\d+)\.\s*(.+)/);
                                  if (numberedMatch) {
                                    return (
                                      <div
                                        key={j}
                                        className="flex items-start gap-3 mb-2 ml-1"
                                      >
                                        <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">
                                          {numberedMatch[1]}
                                        </span>
                                        <span
                                          className="text-text-secondary pt-0.5"
                                          dangerouslySetInnerHTML={{
                                            __html: numberedMatch[2].replace(
                                              /\*\*(.*?)\*\*/g,
                                              '<strong class="text-text-primary">$1</strong>',
                                            ),
                                          }}
                                        />
                                      </div>
                                    );
                                  }

                                  // Bullet point (•)
                                  if (trimmedLine.startsWith("•")) {
                                    return (
                                      <div
                                        key={j}
                                        className="flex items-start gap-2 mb-1.5 ml-2"
                                      >
                                        <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-primary mt-2" />
                                        <span
                                          className="text-text-secondary"
                                          dangerouslySetInnerHTML={{
                                            __html: trimmedLine
                                              .slice(1)
                                              .trim()
                                              .replace(
                                                /\*\*(.*?)\*\*/g,
                                                '<strong class="text-text-primary">$1</strong>',
                                              ),
                                          }}
                                        />
                                      </div>
                                    );
                                  }

                                  // Checkmark (✓)
                                  if (trimmedLine.startsWith("✓")) {
                                    return (
                                      <div
                                        key={j}
                                        className="flex items-start gap-2 mb-1.5 ml-2"
                                      >
                                        <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                        <span
                                          className="text-text-secondary"
                                          dangerouslySetInnerHTML={{
                                            __html: trimmedLine
                                              .slice(1)
                                              .trim()
                                              .replace(
                                                /\*\*(.*?)\*\*/g,
                                                '<strong class="text-text-primary">$1</strong>',
                                              ),
                                          }}
                                        />
                                      </div>
                                    );
                                  }

                                  // Regular line in a list context
                                  return (
                                    <p
                                      key={j}
                                      className="text-text-secondary mb-1"
                                      dangerouslySetInnerHTML={{
                                        __html: trimmedLine.replace(
                                          /\*\*(.*?)\*\*/g,
                                          '<strong class="text-text-primary">$1</strong>',
                                        ),
                                      }}
                                    />
                                  );
                                })}
                              </div>
                            );
                          }

                          // Regular paragraph
                          return (
                            <p
                              key={i}
                              className="text-text-secondary leading-relaxed mb-4"
                              dangerouslySetInnerHTML={{
                                __html: paragraph.replace(
                                  /\*\*(.*?)\*\*/g,
                                  '<strong class="text-text-primary">$1</strong>',
                                ),
                              }}
                            />
                          );
                        })}
                    </div>
                  </div>
                )}

                {/* Navigation between articles */}
                <div className="flex justify-between mt-6">
                  <button
                    onClick={() =>
                      setActiveArticle(Math.max(0, activeArticle - 1))
                    }
                    disabled={activeArticle === 0}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      activeArticle === 0
                        ? "text-text-muted cursor-not-allowed"
                        : "text-primary hover:bg-primary/10"
                    }`}
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Previous
                  </button>
                  <button
                    onClick={() =>
                      setActiveArticle(
                        Math.min(
                          (currentSection.articles?.length || 1) - 1,
                          activeArticle + 1,
                        ),
                      )
                    }
                    disabled={
                      activeArticle ===
                      (currentSection.articles?.length || 1) - 1
                    }
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      activeArticle ===
                      (currentSection.articles?.length || 1) - 1
                        ? "text-text-muted cursor-not-allowed"
                        : "text-primary hover:bg-primary/10"
                    }`}
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}

            {/* No results */}
            {searchQuery && filteredSections.length === 0 && (
              <div className="text-center py-12">
                <Search className="w-12 h-12 text-text-muted mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-text-primary mb-2">
                  No results found
                </h3>
                <p className="text-text-muted">
                  Try searching with different keywords
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function FeedbackPage() {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedbackType, setFeedbackType] = useState("");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [rejectedFiles, setRejectedFiles] = useState([]);
  const fileInputRef = useRef(null);

  const feedbackTypes = [
    {
      id: "bug",
      icon: Bug,
      label: "Bug Report",
      description: "Something isn't working correctly",
    },
    {
      id: "feature",
      icon: Lightbulb,
      label: "Feature Request",
      description: "Suggest a new feature or improvement",
    },
    {
      id: "ux",
      icon: Palette,
      label: "UX Feedback",
      description: "Thoughts on design and usability",
    },
    {
      id: "general",
      icon: MessageSquare,
      label: "General Feedback",
      description: "Any other thoughts or comments",
    },
  ];

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const getFileIcon = (file) => {
    if (file.type.startsWith("image/")) return Image;
    if (file.type.startsWith("video/")) return Film;
    return FileText;
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    addFiles(files);
  };

  const addFiles = (files) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const validFiles = [];
    const rejected = [];

    files.forEach((file) => {
      const isImage = file.type.startsWith("image/");
      const isVideo = file.type.startsWith("video/");
      const isPdf = file.type === "application/pdf";
      const isValidType = isImage || isVideo || isPdf;

      if (!isValidType) {
        rejected.push({
          name: file.name,
          size: file.size,
          reason: "Unsupported file type",
        });
      } else if (file.size > maxSize) {
        rejected.push({
          name: file.name,
          size: file.size,
          reason: "File exceeds 10MB limit",
        });
      } else {
        validFiles.push(file);
      }
    });

    const newAttachments = validFiles.map((file) => ({
      id: `file-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      preview: file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : null,
    }));

    setAttachments((prev) => [...prev, ...newAttachments]);

    // Show rejected files
    if (rejected.length > 0) {
      setRejectedFiles(rejected);
      // Auto-clear after 5 seconds
      setTimeout(() => setRejectedFiles([]), 5000);
    }
  };

  const dismissRejectedFiles = () => {
    setRejectedFiles([]);
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => {
      const attachment = prev.find((a) => a.id === id);
      if (attachment?.preview) {
        URL.revokeObjectURL(attachment.preview);
      }
      return prev.filter((a) => a.id !== id);
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await api.submitFeedback({
        rating,
        feedbackType,
        message,
        attachments,
      });

      // Clean up attachment previews
      attachments.forEach((attachment) => {
        if (attachment.preview) {
          URL.revokeObjectURL(attachment.preview);
        }
      });

      setSubmitted(true);
    } catch (err) {
      console.error("Failed to submit feedback:", err);
      setSubmitError(
        err.message || "Failed to submit feedback. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="p-8 animate-fade-in">
        <div className="max-w-lg mx-auto text-center py-16">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-success/10 mb-6">
            <CheckCircle className="w-10 h-10 text-success" />
          </div>
          <h1 className="text-2xl font-bold text-text-primary mb-4">
            Thank You!
          </h1>
          <p className="text-text-secondary mb-6">
            Your feedback is incredibly valuable to us. We review every
            submission and use it to make Botibonga better for everyone.
          </p>
          <Button
            onClick={() => {
              setSubmitted(false);
              setRating(0);
              setFeedbackType("");
              setMessage("");
              setAttachments([]);
              setSubmitError(null);
            }}
          >
            Submit More Feedback
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 animate-fade-in">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent mb-4">
            <MessageSquarePlus className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-text-primary mb-2">
            We'd Love Your Feedback!
          </h1>
          <p className="text-text-secondary">
            As a beta user, your input shapes the future of Botibonga. Every
            piece of feedback helps us build something amazing.
          </p>
        </div>

        {/* Beta Banner */}
        <div className="bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 rounded-xl p-4 mb-8">
          <div className="flex items-start gap-3">
            <Rocket className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-text-primary">
                You're part of something special!
              </h3>
              <p className="text-sm text-text-secondary mt-1">
                As a beta tester, you're helping us refine and perfect Botibonga
                before the public launch. Your insights directly influence our
                roadmap.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Rating */}
          <div className="card p-6">
            <h2 className="font-semibold text-text-primary mb-4">
              How would you rate your experience so far?
            </h2>
            <div className="flex items-center justify-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-1 transition-transform hover:scale-110"
                >
                  <Star
                    className={`w-10 h-10 transition-colors ${
                      star <= (hoverRating || rating)
                        ? "fill-amber-400 text-amber-400"
                        : "text-gray-300"
                    }`}
                  />
                </button>
              ))}
            </div>
            {rating > 0 && (
              <div className="flex items-center justify-center gap-2 text-sm text-text-muted mt-3">
                {rating === 5 && (
                  <>
                    <PartyPopper className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    <span>Amazing! We're thrilled you love it!</span>
                  </>
                )}
                {rating === 4 && (
                  <>
                    <Smile className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    <span>Great! Thanks for the positive feedback!</span>
                  </>
                )}
                {rating === 3 && (
                  <>
                    <Meh className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    <span>Good to know. How can we improve?</span>
                  </>
                )}
                {rating === 2 && (
                  <>
                    <Wrench className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    <span>We hear you. Please tell us what's not working.</span>
                  </>
                )}
                {rating === 1 && (
                  <>
                    <Heart className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    <span>Sorry to hear that. We want to make it right.</span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Feedback Type */}
          <div className="card p-6">
            <h2 className="font-semibold text-text-primary mb-4">
              What type of feedback do you have?
            </h2>
            <div className="grid grid-cols-2 gap-3">
              {feedbackTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setFeedbackType(type.id)}
                    className={`
                      p-4 rounded-xl border-2 text-left transition-all
                      ${
                        feedbackType === type.id
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50 hover:bg-background"
                      }
                    `}
                  >
                    <span className="flex items-center gap-2 text-base font-medium text-text-primary">
                      <Icon className="w-5 h-5 text-primary flex-shrink-0" />
                      {type.label}
                    </span>
                    <p className="text-xs text-text-muted mt-1">
                      {type.description}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Message */}
          <div className="card p-6">
            <h2 className="font-semibold text-text-primary mb-4">
              Tell us more
            </h2>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Share your thoughts, ideas, or describe any issues you've encountered..."
              className="input-field resize-none"
              rows={5}
            />

            {/* File Attachments */}
            <div className="mt-4">
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`
                  border-2 border-dashed rounded-xl p-4 text-center cursor-pointer
                  transition-all duration-200
                  ${
                    isDragging
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50 hover:bg-gray-50"
                  }
                `}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,video/*,.pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="feedback-file-upload"
                />
                <label
                  htmlFor="feedback-file-upload"
                  className="cursor-pointer"
                >
                  <div className="flex flex-col items-center gap-2">
                    <div
                      className={`p-2 rounded-lg ${isDragging ? "bg-primary/20" : "bg-gray-100"}`}
                    >
                      <Paperclip
                        className={`w-5 h-5 ${isDragging ? "text-primary" : "text-text-muted"}`}
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-text-primary">
                        {isDragging
                          ? "Drop files here"
                          : "Attach screenshots or files"}
                      </p>
                      <p className="text-xs text-text-muted mt-1">
                        Drag & drop or click to browse (Images, Videos, PDF -
                        Max 10MB each)
                      </p>
                    </div>
                  </div>
                </label>
              </div>

              {/* Rejected Files Warning */}
              {rejectedFiles.length > 0 && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl animate-slide-up">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-red-800">
                          {rejectedFiles.length} file
                          {rejectedFiles.length > 1 ? "s" : ""} couldn't be
                          uploaded
                        </p>
                        <p className="text-sm text-red-600 mt-1">
                          Files must be 10MB or less.
                        </p>
                        <ul className="mt-2 space-y-1">
                          {rejectedFiles.map((file, index) => (
                            <li key={index} className="text-sm text-red-700">
                              • {file.name} ({formatFileSize(file.size)}) -{" "}
                              {file.reason}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={dismissRejectedFiles}
                      className="p-1 text-red-400 hover:text-red-600 hover:bg-red-100 rounded transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Attached Files Preview */}
              {attachments.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium text-text-primary">
                    Attached files ({attachments.length})
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {attachments.map((attachment) => {
                      const FileIcon = getFileIcon(attachment);
                      return (
                        <div
                          key={attachment.id}
                          className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg group"
                        >
                          {attachment.preview ? (
                            <img
                              src={attachment.preview}
                              alt={attachment.name}
                              className="w-10 h-10 rounded object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center">
                              <FileIcon className="w-5 h-5 text-text-muted" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-text-primary truncate">
                              {attachment.name}
                            </p>
                            <p className="text-xs text-text-muted">
                              {formatFileSize(attachment.size)}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeAttachment(attachment.id)}
                            className="p-1.5 text-text-muted hover:text-red-500 hover:bg-red-50
                                       rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <p className="text-xs text-text-muted mt-3">
              Be as detailed as you'd like. Screenshots and specific examples
              are always helpful!
            </p>
          </div>

          {/* Error Message */}
          {submitError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-700">{submitError}</p>
              </div>
            </div>
          )}

          {/* Submit */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-text-muted">
              All feedback is reviewed by our team
            </p>
            <Button
              type="submit"
              icon={isSubmitting ? Loader2 : Send}
              disabled={!message.trim() || isSubmitting}
              className={isSubmitting ? "[&_svg]:animate-spin" : ""}
            >
              {isSubmitting ? "Submitting..." : "Submit Feedback"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
